import { BoxProps, TableRowProps } from '@mui/material';
import { CSSProperties } from 'react';
import { DraggableProvided, DraggableRubric, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { CfDataGridRowType, CfDraggableRowData, NavigableElement } from '../../../CfDataGridProps';
import {
  CfDataGridDragDropCustomRow,
  CfDataGridDragDropRow,
  CfDataGridDragDropRowFooter,
} from './CfDataGridDragDropRow';
import { CfDataGridRowProps } from './common';

// If row is draggable, combine dragHandleProps with draggableProps, otherwise dragHandleProps gets passed to column's value getter to be manually set
const getDragDropRowProps = (
  isRowDraggable: boolean,
  draggableProps: BoxProps,
  dragHandleProps: BoxProps | undefined
): { draggableProps: BoxProps; dragHandleProps: BoxProps | undefined } =>
  isRowDraggable
    ? { draggableProps: { ...draggableProps, ...dragHandleProps }, dragHandleProps: undefined }
    : { draggableProps, dragHandleProps };

const getStyle = (
  row: CfDraggableRowData,
  provided: DraggableProvided,
  snapshot: DraggableStateSnapshot,
  style?: CSSProperties
) => {
  //console.log(row, snapshot);

  return { ...provided.draggableProps.style, ...style };
};

export const GetDragDropRow = (
  row: CfDraggableRowData,
  provided: DraggableProvided,
  snapshot: DraggableStateSnapshot,
  rubric: DraggableRubric,
  style?: CSSProperties
): JSX.Element =>
  row.getContent(
    {
      ...provided.draggableProps,
      ref: provided.innerRef,
      style: getStyle(row, provided, snapshot, style),
    } as TableRowProps,
    provided.dragHandleProps || undefined
  );

export function GetDraggableRowData<TItem>(
  props: CfDataGridRowProps<TItem>,
  itemId: string | undefined,
  getRowHeight: (item: TItem | undefined, type: CfDataGridRowType) => number,
  isRowDraggable: boolean
): CfDraggableRowData[] {
  const rows = [] as CfDraggableRowData[];

  const item = props.item;
  if (!item || !itemId) {
    return [];
  }

  const customRowBefore = props.tableViewDefinition.insertCustomRow?.(
    { item: props.previousItem, itemIndex: props.itemIndex - 1 },
    { item: props.item, itemIndex: props.itemIndex },
    undefined,
    undefined
  );

  const customRowAfter =
    props.itemIndex === props.itemCount - 1
      ? props.tableViewDefinition.insertCustomRow?.(
          { item: props.item, itemIndex: props.itemIndex },
          { item: undefined, itemIndex: props.itemCount },
          undefined,
          undefined
        )
      : undefined;

  const footerRow = props.tableViewDefinition.insertFooterRow?.({
    item: item,
    itemIndex: props.itemIndex,
    recomputeRowHeights: undefined,
    recomputeRowHeight: undefined,
    setNavigableElement: (navigableElement: NavigableElement, columnIndex?: number) =>
      props.setNavigableElement(navigableElement, props.itemIndex, columnIndex ?? 0),
  });

  const customRowBeforeHeight = getRowHeight(item, 'custom-row-before');
  if (customRowBefore && customRowBeforeHeight !== 0) {
    rows.push({
      type: 'custom-row-before',
      getContent: (draggableProps: BoxProps, dragHandleProps: BoxProps | undefined) =>
        (
          <CfDataGridDragDropCustomRow
            content={customRowBefore.content}
            tableRowSxProps={customRowBefore.tableRowSxProps}
            {...getDragDropRowProps(isRowDraggable, draggableProps, dragHandleProps)}
          />
        ) as JSX.Element,
      id: props.itemIndex + '-custom-row-before',
      itemId: itemId,
      itemIndex: props.itemIndex,
      rowHeight: customRowBeforeHeight,
    });
  }

  const rowHeight = getRowHeight(props.item, 'row');
  if (rowHeight !== 0) {
    rows.push({
      type: 'row',
      getContent: (draggableProps: BoxProps, dragHandleProps: BoxProps | undefined) =>
        (
          <CfDataGridDragDropRow
            rowProps={props}
            {...getDragDropRowProps(isRowDraggable, draggableProps, dragHandleProps)}
          />
        ) as JSX.Element,
      id: props.itemIndex + '-row',
      itemId: itemId,
      itemIndex: props.itemIndex,
      rowHeight: rowHeight,
    });
  }

  const footerHeight = getRowHeight(props.item, 'row-footer');
  if (footerRow && footerHeight !== 0) {
    rows.push({
      type: 'row-footer',
      getContent: (draggableProps: BoxProps, dragHandleProps: BoxProps | undefined) =>
        (
          <CfDataGridDragDropRowFooter
            content={footerRow.content}
            tableRowSxProps={footerRow.tableRowSxProps}
            {...getDragDropRowProps(isRowDraggable, draggableProps, dragHandleProps)}
          />
        ) as JSX.Element,
      id: props.itemIndex + '-row-footer',
      itemId: itemId,
      itemIndex: props.itemIndex,
      rowHeight: footerHeight,
    });
  }

  const customRowAfterHeight = getRowHeight(props.item, 'custom-row-after');
  if (customRowAfter) {
    rows.push({
      type: 'custom-row-after',
      getContent: (draggableProps: BoxProps, dragHandleProps: BoxProps | undefined) =>
        (
          <CfDataGridDragDropCustomRow
            content={customRowAfter.content}
            tableRowSxProps={customRowAfter.tableRowSxProps}
            {...getDragDropRowProps(isRowDraggable, draggableProps, dragHandleProps)}
          />
        ) as JSX.Element,
      id: props.itemIndex + '-custom-row-after',
      itemId: itemId,
      itemIndex: props.itemIndex,
      rowHeight: customRowAfterHeight,
    });
  }

  return rows;
}
